import { create } from "zustand";
import { isScriptRunning, type WorktreeScript } from "@rivets/core";

/**
 * Per-worktree view of the repository's scripts, fed by `script.state`.
 *
 * The worker sends the whole set on every change, so this store replaces rather
 * than merges: a script the repository removed has to disappear, and a state
 * the worker no longer reports is not one worth remembering. Nothing here is
 * persisted — the worker is the authority and republishes on every reconnect.
 */

export interface WorktreeScriptSetState {
  scripts: WorktreeScript[];
  problems: string[];
  /** False until the worker has sent anything for this worktree, so the UI can
   * tell "no scripts" apart from "not asked yet". */
  known: boolean;
}

const EMPTY: WorktreeScriptSetState = { scripts: [], problems: [], known: false };

interface ScriptStore {
  byWorktree: Record<string, WorktreeScriptSetState>;
  apply: (worktreeId: string, scripts: WorktreeScript[], problems: string[]) => void;
  reset: () => void;
}

export const useScriptStore = create<ScriptStore>((set, get) => ({
  byWorktree: {},
  apply(worktreeId, scripts, problems) {
    set({ byWorktree: { ...get().byWorktree, [worktreeId]: { scripts, problems, known: true } } });
  },
  reset() {
    set({ byWorktree: {} });
  },
}));

/** Stable empty value so a selector for an unknown worktree does not churn. */
export function selectWorktreeScripts(worktreeId: string) {
  return (state: ScriptStore): WorktreeScriptSetState => state.byWorktree[worktreeId] ?? EMPTY;
}

/** Scripts that get a terminal tab: everything the repository declared that has
 * ever produced output, plus whatever is running now. A `run` script nobody has
 * pressed yet has no terminal and no tab — it lives in the Run menu until then. */
export function scriptTabs(state: WorktreeScriptSetState): WorktreeScript[] {
  return state.scripts.filter((script) => script.terminalId !== undefined);
}

/**
 * Scripts a user may start from the Run menu.
 *
 * `archive` is excluded: it runs when a workspace is archived, and running it
 * by hand would tear down a workspace somebody is still using. `hidden` is the
 * repository asking for a script to stay out of the menu while remaining
 * runnable by id — it still keeps its output tab once it has run.
 */
export function runnableScripts(state: WorktreeScriptSetState): WorktreeScript[] {
  return state.scripts.filter((script) => script.kind !== "archive" && !script.hidden);
}

/** What the Run button triggers when the user has not picked: the repository's
 * declared default, else the first run script, else setup. */
export function preferredScript(scripts: WorktreeScript[]): WorktreeScript | undefined {
  return scripts.find((script) => script.isDefault)
    ?? scripts.find((script) => script.kind === "run")
    ?? scripts[0];
}

/** The process is alive: running, or stalled in its run. Stop applies. */
export function isScriptBusy(script: Pick<WorktreeScript, "state">): boolean {
  return isScriptRunning(script.state);
}

/** A setup or archive script silent for 2 minutes (native `stalledLabel`). */
export const SCRIPT_STALLED_LABEL = "Stalled — no output for 2 min";

/** One-word status for a tab badge. */
export function scriptStatusLabel(script: Pick<WorktreeScript, "state" | "exitCode">): string {
  switch (script.state) {
    case "running": return "Running";
    case "stalled": return SCRIPT_STALLED_LABEL;
    case "succeeded": return "Done";
    case "failed": return script.exitCode === undefined ? "Failed" : `Exit ${script.exitCode}`;
    case "timeout": return "Timed out";
    case "canceled": return "Stopped";
    case "unavailable": return "Unavailable";
    default: return "Idle";
  }
}

/**
 * Maps a script's outcome onto the status-dot classes the terminal tabs
 * already use, so a script pill and a shell pill read the same way.
 *
 * `is-idle` hides the dot: a dot on every tab is a dot that says nothing, so
 * only a run that is working or that went wrong shows one.
 */
export function scriptStatusClass(state: WorktreeScript["state"]): string {
  switch (state) {
    case "running": return "opening";
    // The base dot, muted: still running, nothing to alarm about yet.
    case "stalled": return "is-stalled";
    case "failed":
    case "timeout":
    case "unavailable": return "failed";
    default: return "is-idle";
  }
}
